import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~/app/auth/context/AuthContext';
import { OperatorPortfolioService } from '~/app/services/operator-portfolio/operator-portfolio.service';
import { PortfolioKpiService } from '~/app/services/operator-portfolio/portfolio-kpi.service';
import type { PortfolioPageViewProps } from '../PortfolioPage.types';
import {
  PORTFOLIO_PAGE_SIZE,
  filterPropertyRows,
  mapAttentionStat,
  mapClaimablePenalties,
  mapComplianceStat,
  mapPenaltyExposure,
  mapPropertyRows,
  paginateRows,
} from '../utils';
import { usePortfolioPropertiesColumns } from './usePortfolioPropertiesColumns';

export function usePortfolioPage(): PortfolioPageViewProps {
  const { t, i18n } = useTranslation('dashboard');
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';
  const columns = usePortfolioPropertiesColumns();

  const [searchValue, setSearchValue] = useState('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [page, setPage] = useState(1);

  const kpisQuery = useQuery({
    queryKey: ['operator-portfolio', 'kpis', organizationId],
    queryFn: () => PortfolioKpiService.getKpis(),
    enabled: !!organizationId,
  });

  const propertiesQuery = useQuery({
    queryKey: ['operator-portfolio', 'properties', organizationId],
    queryFn: () => OperatorPortfolioService.getProperties(),
    enabled: !!organizationId,
  });

  const kpis = kpisQuery.data;
  const properties = propertiesQuery.data;
  const hasOrganization = !!organizationId;

  const allRows = useMemo(
    () => (properties ? mapPropertyRows(properties, t) : []),
    [properties, t],
  );

  const filteredRows = useMemo(
    () => filterPropertyRows(allRows, searchValue, attentionOnly),
    [allRows, searchValue, attentionOnly],
  );

  const pageCount = Math.max(
    1,
    Math.ceil(filteredRows.length / PORTFOLIO_PAGE_SIZE),
  );
  const safePage = Math.min(page, pageCount);
  const pageRows = useMemo(
    () => paginateRows(filteredRows, safePage, PORTFOLIO_PAGE_SIZE),
    [filteredRows, safePage],
  );

  const onSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const onAttentionFilterChange = useCallback((active: boolean) => {
    setAttentionOnly(active);
    setPage(1);
  }, []);

  const onGenerateReport = useCallback(() => {
    // Report export lands in a follow-up ticket.
  }, []);

  return {
    isLoading: hasOrganization && kpisQuery.isLoading,
    isError: !hasOrganization || kpisQuery.isError,
    errorTitle: t('errors.loadTitle'),
    errorMessage: !hasOrganization
      ? t('errors.missingOrganization')
      : kpisQuery.error instanceof Error
        ? kpisQuery.error.message
        : t('errors.loadFallback'),
    pageTitle: t('portfolioPage.title'),
    scopeLabel: t('portfolioPage.scope', {
      chargers: kpis?.chargerCount ?? 0,
      sites: kpis?.siteCount ?? 0,
    }),
    generateReportLabel: t('portfolioPage.generateReport'),
    attentionStat: kpis
      ? mapAttentionStat(kpis.sitesNeedingAttention, t)
      : null,
    complianceStat: kpis ? mapComplianceStat(kpis.contractCompliance, t) : null,
    claimablePenalties: kpis
      ? mapClaimablePenalties(kpis, i18n.language, t)
      : null,
    penaltyExposure: kpis ? mapPenaltyExposure(kpis, i18n.language, t) : null,
    propertiesTitle: t('portfolioPage.propertiesTitle'),
    searchPlaceholder: t('portfolioPage.searchPlaceholder'),
    searchValue,
    onSearchChange,
    attentionFilterLabel: t('portfolioPage.attentionFilter'),
    attentionFilterActive: attentionOnly,
    onAttentionFilterChange,
    propertiesColumns: columns,
    propertiesRows: pageRows,
    alertMessage: properties?.alert?.message ?? null,
    showingLabel: t('portfolioPage.showing', {
      shown: pageRows.length,
      total: filteredRows.length,
    }),
    pageLabel: t('portfolioPage.page', {
      page: safePage,
      pageCount,
    }),
    prevPageAriaLabel: t('portfolioPage.prevPage'),
    nextPageAriaLabel: t('portfolioPage.nextPage'),
    page: safePage,
    pageCount,
    onPageChange: setPage,
    onGenerateReport,
  };
}
